import { useState, useRef, useEffect } from 'react';
import { ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import { useDebouncedValue } from '@/hooks/common';

interface MultiSelectDropdownBaseProps {
  options: string[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  pinSelectedToTop?: boolean;
  showClearAll?: boolean;
  /**
   * Optional value→label map for display only. Selection and onChange keep
   * operating on the raw option values; the rendered text and the search query
   * both match against the label. Omitted → labels equal values (back-compat
   * for existing callers).
   */
  optionLabels?: Record<string, string>;
}

/** Default mode: `options` is the whole list and the search box filters it locally. */
interface LocalFilteringProps {
  onSearchChange?: undefined;
  isLoading?: undefined;
  isError?: undefined;
  hasMore?: undefined;
  onLoadMore?: undefined;
  isLoadingMore?: undefined;
  loadMoreFailed?: undefined;
}

/**
 * Server mode, the multi-select twin of `SearchableSelect`'s: the caller searches and
 * pages, `options` is the answer to the current query. `isLoading` and `isError` are
 * required for the same reason as there: without them an in-flight or failed search
 * renders as "No results found".
 */
interface ServerSearchProps {
  /** Debounced, trimmed query; called on query changes only, so an inline arrow is safe. */
  onSearchChange: (query: string) => void;
  isLoading: boolean;
  isError: boolean;
  hasMore?: boolean;
  /** Called when the list is scrolled close to its end, or from the "Load more" row. */
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
  /** The last page request failed: scrolling no longer retries it, the "Load more" row does. */
  loadMoreFailed?: boolean;
}

type MultiSelectDropdownProps = MultiSelectDropdownBaseProps & (LocalFilteringProps | ServerSearchProps);

export const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  options,
  selectedValues,
  onChange,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  pinSelectedToTop = false,
  showClearAll = true,
  optionLabels,
  onSearchChange,
  isLoading = false,
  isError = false,
  hasMore = false,
  onLoadMore,
  isLoadingMore = false,
  loadMoreFailed = false,
}) => {
  const labelOf = (option: string): string => optionLabels?.[option] ?? option;
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const isServerMode = onSearchChange !== undefined;
  const debouncedQuery = useDebouncedValue(searchQuery, 250);
  // A ref, so the report fires on query changes only and not on every parent render.
  const onSearchChangeRef = useRef(onSearchChange);
  useEffect(() => {
    onSearchChangeRef.current = onSearchChange;
  });
  useEffect(() => {
    onSearchChangeRef.current?.(debouncedQuery.trim());
  }, [debouncedQuery]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const selectAllRef = useRef<HTMLInputElement>(null);
  // Snapshot of selectedValues at the moment the dropdown opens. Used to
  // partition options into pinned-selected / unselected groups so that toggling
  // a checkbox while the dropdown is open does NOT reorder rows out from under
  // the user's cursor. The snapshot refreshes on each new open. Pattern used
  // by Excel, Google Sheets, GitHub label picker.
  const [stableSelectedAtOpen, setStableSelectedAtOpen] = useState<string[]>(selectedValues);
  const selectedValuesRef = useRef(selectedValues);
  selectedValuesRef.current = selectedValues;
  useEffect(() => {
    if (isOpen) setStableSelectedAtOpen(selectedValuesRef.current);
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Server mode: the server already matched the query, on fields the option may not carry.
  const filteredOptions = isServerMode
    ? options
    : options.filter((option) => labelOf(option).toLowerCase().includes(searchQuery.toLowerCase()));

  const { selectedOptions, unselectedOptions } = isServerMode
    ? (() => {
        // The selection is listed whatever the query: a selected value is usually not on
        // the loaded page, and an unlisted one could not be unticked. One ticked while open
        // joins the pinned group only once it drops out of `options`, so rows never jump
        // under the cursor.
        const pinned = [
          ...stableSelectedAtOpen,
          ...selectedValues.filter((v) => !stableSelectedAtOpen.includes(v) && !options.includes(v)),
        ];
        return { selectedOptions: pinned, unselectedOptions: options.filter((opt) => !pinned.includes(opt)) };
      })()
    : pinSelectedToTop
    ? {
        selectedOptions: filteredOptions.filter((opt) => stableSelectedAtOpen.includes(opt)),
        unselectedOptions: filteredOptions.filter((opt) => !stableSelectedAtOpen.includes(opt)),
      }
    : { selectedOptions: [], unselectedOptions: filteredOptions };

  const visibleSelectedCount = filteredOptions.reduce(
    (acc, opt) => (selectedValues.includes(opt) ? acc + 1 : acc),
    0
  );
  const allVisibleSelected =
    filteredOptions.length > 0 && visibleSelectedCount === filteredOptions.length;
  const someVisibleSelected = visibleSelectedCount > 0 && !allVisibleSelected;

  useEffect(() => {
    if (!isOpen) return;
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someVisibleSelected;
    }
  }, [someVisibleSelected, isOpen]);

  const toggleOption = (option: string) => {
    if (selectedValues.includes(option)) {
      onChange(selectedValues.filter((v) => v !== option));
    } else {
      onChange([...selectedValues, option]);
    }
  };

  const toggleSelectAll = () => {
    if (allVisibleSelected) {
      const visible = new Set(filteredOptions);
      onChange(selectedValues.filter((v) => !visible.has(v)));
    } else {
      const merged = new Set(selectedValues);
      for (const opt of filteredOptions) merged.add(opt);
      onChange(Array.from(merged));
    }
  };

  const clearAll = () => {
    onChange([]);
  };

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (!hasMore || isLoadingMore || loadMoreFailed) return;
    const el = event.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 48) onLoadMore?.();
  };

  const renderOptionRow = (option: string) => (
    <label key={option} className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer">
      <input
        type="checkbox"
        checked={selectedValues.includes(option)}
        onChange={() => toggleOption(option)}
        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
      />
      <span className="ml-2 text-sm text-gray-900 truncate" title={labelOf(option)}>
        {labelOf(option)}
      </span>
    </label>
  );

  const renderServerList = () => (
    <>
      {selectedOptions.map(renderOptionRow)}
      {selectedOptions.length > 0 && (isLoading || isError || unselectedOptions.length > 0) && (
        <div className="border-t border-gray-300 my-1" />
      )}
      {isLoading ? (
        <div className="px-3 py-2 text-sm text-gray-500 text-center" role="status">
          Loading…
        </div>
      ) : unselectedOptions.length === 0 ? (
        isError ? (
          <div className="px-3 py-2 text-sm text-red-600 text-center" role="alert">
            Search failed, try again
          </div>
        ) : (
          selectedOptions.length === 0 && (
            <div className="px-3 py-2 text-sm text-gray-500 text-center">No results found</div>
          )
        )
      ) : (
        <>
          {unselectedOptions.map(renderOptionRow)}
          {isLoadingMore && (
            <div className="px-3 py-2 text-sm text-gray-500 text-center" role="status">
              Loading more…
            </div>
          )}
          {loadMoreFailed && !isLoadingMore && (
            <div className="px-3 py-2 text-sm text-red-600 text-center" role="alert">
              Could not load more
            </div>
          )}
          {/* The operable twin of the scroll trigger, for keyboard and screen-reader users. */}
          {hasMore && !isLoadingMore && (
            <button
              type="button"
              onClick={() => onLoadMore?.()}
              className="w-full px-3 py-2 text-sm text-blue-700 hover:bg-gray-50"
            >
              Load more
            </button>
          )}
        </>
      )}
    </>
  );

  return (
    <div ref={dropdownRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={clsx(
          'w-full flex items-center justify-between px-3 py-2 text-sm border rounded-md bg-white',
          'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
          'hover:bg-gray-50 transition-colors',
          isOpen ? 'border-blue-500 ring-2 ring-blue-500' : 'border-gray-300'
        )}
      >
        <span
          className={clsx(
            'truncate',
            selectedValues.length === 0 ? 'text-gray-400' : 'text-gray-900'
          )}
          title={selectedValues.length === 1 ? labelOf(selectedValues[0]) : undefined}
        >
          {selectedValues.length === 0
            ? placeholder
            : selectedValues.length === 1
            ? labelOf(selectedValues[0])
            : `${selectedValues.length} selected`}
        </span>
        <ChevronDownIcon
          className={clsx(
            'h-4 w-4 text-gray-400 transition-transform flex-shrink-0 ml-2',
            isOpen && 'transform rotate-180'
          )}
        />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-[300px] flex flex-col">
          <div className="p-2 border-b border-gray-200 bg-gray-50 sticky top-0">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={searchPlaceholder}
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            {showClearAll && selectedValues.length > 0 && (
              <button
                onClick={clearAll}
                className="mt-2 w-full text-xs text-blue-600 hover:text-blue-800 font-medium text-left"
              >
                Clear All
              </button>
            )}
          </div>

          <div
            className="overflow-y-auto flex-1"
            onScroll={isServerMode ? handleScroll : undefined}
            aria-busy={isServerMode ? isLoading || isLoadingMore : undefined}
          >
            {isServerMode ? (
              renderServerList()
            ) : filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500 text-center">
                No results found
              </div>
            ) : (
              <>
                <label className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-200 bg-gray-50">
                  <input
                    type="checkbox"
                    ref={selectAllRef}
                    checked={allVisibleSelected}
                    onChange={toggleSelectAll}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-900">
                    Select all
                  </span>
                </label>
                {pinSelectedToTop && selectedOptions.length > 0 && (
                  <>
                    {selectedOptions.map(renderOptionRow)}
                    {unselectedOptions.length > 0 && (
                      <div className="border-t border-gray-300 my-1" />
                    )}
                  </>
                )}
                {(pinSelectedToTop ? unselectedOptions : filteredOptions).map(renderOptionRow)}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

